import { PersonSearchParamsv2, RelatedReport } from '@ltvco/refresh-lib/v2';

import { PermalinkOrTeaserOrParamsResult } from 'components/ChatBV/types';

import { SEARCH_TYPES } from '../constants';

import {
  getPersonPermalink,
  getPhoneNumberPermalink,
  getEmailPermalink,
  getPropertyPermalink,
  getVehiclePermalink,
} from './permalinkUtils';

export interface TransformedReportData {
  username?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  vin?: string;
  licensePlate?: string;
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  bvid?: string;
  permalink?: string;
  zipcode?: string;
}

export interface RelatedReportTransformResult {
  reportType: string;
  data?: TransformedReportData;
  personSearch?: PersonSearchParamsv2;
  useBvid?: boolean;
}

const buildAddress = (parsedData: any) => {
  const parts = [
    parsedData?.primary_address_number,
    parsedData?.pre_direction,
    parsedData?.street_name,
    parsedData?.street_type,
    parsedData?.post_direction,
    parsedData?.unit_number,
  ]
    .filter(Boolean)
    .join(' ');

  return parts || '';
};

export const getRelatedReportData = (
  relatedReport: RelatedReport
): RelatedReportTransformResult => {
  const { data, reportType } = relatedReport;
  const bvid = data?.bvId;

  switch (reportType) {
    case SEARCH_TYPES.PERSON: {
      if (bvid) {
        return {
          reportType,
          data: { bvid },
          useBvid: true,
        };
      }
      const personSearch: PersonSearchParamsv2 = {
        fName:
          data?.identity?.names?.[0]?.parsed?.first ||
          data?.query_params?.fname ||
          '',
        mName:
          data?.identity?.names?.[0]?.parsed?.middle ||
          data?.query_params?.mn ||
          '',
        lName:
          data?.identity?.names?.[0]?.parsed?.last ||
          data?.query_params?.ln ||
          '',
        age: data?.age || data?.query_params?.age || '',
        city:
          data?.contact?.addresses?.[0]?.parsed?.city ||
          data?.query_params?.city ||
          '',
        state:
          data?.contact?.addresses?.[0]?.parsed?.state ||
          data?.query_params?.state ||
          'All',
      };
      return {
        reportType: 'teaser',
        personSearch,
      };
    }
    case SEARCH_TYPES.PHONE: {
      const phoneData: TransformedReportData = {
        phoneNumber: data?.number,
      };
      return {
        reportType,
        data: phoneData,
      };
    }
    case SEARCH_TYPES.EMAIL: {
      const emailData: TransformedReportData = {
        email: data?.email,
      };
      return {
        reportType,
        data: emailData,
      };
    }
    case SEARCH_TYPES.PROPERTY: {
      const propertyData: TransformedReportData = {
        address: data?.parsed ? buildAddress(data?.parsed) : undefined,
        city: data?.parsed?.city,
        state: data?.parsed?.state,
        zipcode: data?.parsed?.zip5,
      };
      return {
        reportType,
        data: propertyData,
      };
    }
    case SEARCH_TYPES.VEHICLE: {
      const vehicleData: TransformedReportData = {
        vin: data?.vin,
        licensePlate: data?.license_plate,
        state: data?.state,
        year: data?.year,
        make: data?.make,
        model: data?.model,
        trim: data?.trim,
      };
      return {
        reportType,
        data: vehicleData,
      };
    }
    case SEARCH_TYPES.OBITUARY: {
      const obituaryData = {
        permalink: data?.permalink,
        firstName: data?.identity?.names?.[0]?.parsed?.first,
        lastName: data?.identity?.names?.[0]?.parsed?.last,
        birthYear: data?.identity?.dobs?.[0]?.date?.parsed?.year,
        deathYear: data?.identity?.dods?.[0]?.date?.parsed?.year,
        city: data?.contact?.addresses?.[0]?.parsed?.city,
        state: data?.contact?.addresses?.[0]?.parsed?.state,
      };

      if (obituaryData.permalink) {
        return {
          reportType,
          data: { permalink: obituaryData.permalink },
        };
      } else if (obituaryData.firstName && obituaryData.lastName) {
        const personSearch: PersonSearchParamsv2 = {
          fName: obituaryData.firstName,
          lName: obituaryData.lastName,
          city: obituaryData.city,
          state: obituaryData.state || 'All',
          age: '',
          birthYear: obituaryData.birthYear,
          deathYear: obituaryData.deathYear,
        };
        return {
          reportType: 'teaser',
          personSearch,
        };
      }
      return {
        reportType,
        data: {},
      };
    }
    case SEARCH_TYPES.USERNAME: {
      const usernameData: TransformedReportData = {
        username: data?.username,
      };
      return {
        reportType,
        data: usernameData,
      };
    }
    default:
      return {
        reportType,
        data: {},
      };
  }
};

export async function getRelatedReportPermalink(
  reportType: string,
  data: TransformedReportData
): Promise<PermalinkOrTeaserOrParamsResult> {
  switch (reportType) {
    case SEARCH_TYPES.PERSON:
      if (data.bvid) {
        return await getPersonPermalink(data.bvid);
      }
      return { error: 'BVID required for person reports' };

    case SEARCH_TYPES.OBITUARY:
      if (data.permalink) {
        return { permalink: data.permalink };
      }
      return { error: 'Permalink required for obituary reports' };

    case SEARCH_TYPES.PHONE:
      if (data.phoneNumber) {
        return await getPhoneNumberPermalink(data.phoneNumber);
      }
      return { error: 'Phone number required for phone reports' };

    case SEARCH_TYPES.EMAIL:
      if (data.email) {
        return await getEmailPermalink(data.email);
      }
      return { error: 'Email required for email reports' };

    case SEARCH_TYPES.PROPERTY:
      if (data.address) {
        return await getPropertyPermalink(
          data.address,
          data.city,
          data.state,
          data.zipcode
        );
      }
      return { error: 'Address required for property reports' };

    case SEARCH_TYPES.VEHICLE:
      if (data.vin) {
        return await getVehiclePermalink(data);
      }
      if (data.licensePlate && data.state) {
        return await getVehiclePermalink({
          licensePlate: data.licensePlate,
          state: data.state,
        });
      }
      if (data.year && data.make && data.model && data.trim) {
        return await getVehiclePermalink({
          year: data.year,
          make: data.make,
          model: data.model,
          trim: data.trim,
        });
      }
      return { error: 'Invalid vehicle search parameters' };

    default:
      return { error: `Unsupported report type: ${reportType}` };
  }
}
